import { useQuery, useMutation, type UseMutationOptions } from "@tanstack/react-query";
import { useAppConfig } from "providers";
import { type UploadResponse } from "components/ProviderProfile/interfaces";
import { FileUploadController, type FileUploadSearchParams } from "../controllers/fileUploadController";
import { onError } from "api/error/errorHandler";
import { type UploadPayload } from "../types";

// Initialize controller with API host
const useController = () => {
    const { REACT_APP_FILEUPLOAD_HOST } = useAppConfig();
    return new FileUploadController(REACT_APP_FILEUPLOAD_HOST);
};

// Query hooks
export const useSearchFiles = (params?: FileUploadSearchParams, version = 'v1') => {
    const controller = useController();
    return useQuery({
        queryKey: ['files', 'search', params],
        queryFn: () => controller.searchFiles(params, version),
        enabled: !!params?.Id && !!params?.Type
    });
};

export const useGetFileById = (fileId: string, version = 'v1') => {
    const controller = useController();
    return useQuery({
        queryKey: ['files', 'single', fileId],
        queryFn: () => controller.getFileById(fileId, version),
        enabled: !!fileId
    });
};

// Mutation hooks
export const useUploadFile = (options: UseMutationOptions<UploadResponse, Error, UploadPayload>, version = 'v1') => {
    const controller = useController();
    return useMutation({
        mutationFn: (payload: UploadPayload) => controller.uploadFile(payload, version),
        mutationKey: ['files', 'upload'],
        ...options,
        onError: typeof options?.onError === 'function' ? options.onError : onError
    });
};

export const useUpdateFile = (options: UseMutationOptions<UploadResponse, Error, UploadPayload>, version = 'v1') => {
    const controller = useController();
    return useMutation({
        mutationFn: (payload: UploadPayload) => controller.updateFile(payload, version),
        mutationKey: ['files', 'update'],
        ...options,
        onError: typeof options?.onError === 'function' ? options.onError : onError
    });
};

export const useDeleteFile = (options: UseMutationOptions<void, Error, string>, version = 'v1') => {
    const controller = useController();
    return useMutation({
        mutationFn: (fileId: string) => controller.deleteFile(fileId, version),
        mutationKey: ['files', 'delete'],
        ...options,
        onError: typeof options?.onError === 'function' ? options.onError : onError
    });
};
